import React, { forwardRef, useState } from 'react';

import clsx from 'clsx';
import { Message } from 'react-hook-form';
import NumberFormat, { NumberFormatProps, NumberFormatValues } from 'react-number-format';

import InputError from 'components/Inputs/InputError';

import styles from './NumberInput.module.scss';

const DECIMAL_PLACES_AMOUNT = 0;

interface NumberInputProps extends NumberFormatProps {
  label: string;
  errorMessage?: Message | undefined;
  decimalPlacesAmount?: number;
  disabled?: boolean;
  value?: string | number;
  isAllowed?: (values: NumberFormatValues) => boolean;
  format?: string;
  allowEmptyFormatting?: boolean;
  inputClassName?: string;
  labelClassName?: string;
  inputContainerClassName?: string;
}

const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      prefix,
      label,
      onChange,
      placeholder,
      className,
      inputClassName,
      labelClassName,
      inputContainerClassName,
      allowNegative,
      thousandSeparator,
      suffix,
      errorMessage,
      onFocus,
      onBlur,
      name,
      maxLength,
      max,
      disabled,
      value,
      isAllowed,
      format,
      decimalPlacesAmount = DECIMAL_PLACES_AMOUNT,
      allowEmptyFormatting,
      pattern,
      ...rest
    },
    ref,
  ) => {
    const [isFocused, setFocus] = useState(false);
    const toggleFocusInput = () => {
      setFocus(!isFocused);
    };

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      toggleFocusInput();

      if (onFocus) {
        onFocus(event);
      }
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      toggleFocusInput();

      if (onBlur) {
        onBlur(event);
      }
    };

    return (
      <div className={className}>
        <div className={clsx(styles.inputContainer, inputContainerClassName)}>
          {label && <p className={clsx(styles.label, labelClassName)}>{label}</p>}
          <NumberFormat
            getInputRef={ref}
            className={clsx(styles.input, inputClassName, !!errorMessage && styles.error)}
            prefix={prefix}
            suffix={suffix}
            onChange={onChange}
            placeholder={placeholder}
            allowNegative={allowNegative}
            thousandSeparator={thousandSeparator}
            decimalScale={decimalPlacesAmount}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            name={name}
            maxLength={maxLength}
            max={max}
            disabled={disabled}
            value={value}
            isAllowed={isAllowed}
            format={format}
            pattern={pattern}
            allowEmptyFormatting={isFocused && allowEmptyFormatting}
            {...rest}
          />
          {!!errorMessage && <InputError errorMessage={errorMessage} />}
        </div>
      </div>
    );
  },
);

export default NumberInput;
